/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from 'react'

/**
 * Get displayName static property from react component
 *
 * @param WrappedComponent
 * @return {String}
 */
export const getDisplayName = (WrappedComponent: React.ComponentType<any>) =>
    WrappedComponent.displayName || WrappedComponent.name || 'Component'

/**
 * Create html markup for React element tag
 *
 * @param {String} markup
 * @return {Object} _html object attribute markupped
 */
export const createMarkup = (markup: string) => ({ __html: markup })

/**
 * Create a context with hook useState
 *
 * @param {Any} defaultValue
 * @return {Array} [Context, Provider]
 */
export function createStateCtx<A>(defaultValue: A) {
    type UpdateType = React.Dispatch<React.SetStateAction<typeof defaultValue>>

    const defaultUpdate: UpdateType = () => defaultValue

    const ctx = React.createContext({ state: defaultValue, update: defaultUpdate })

    function Provider(props: React.PropsWithChildren<{}>) {
        const [state, update] = React.useState(defaultValue)
        return <ctx.Provider value={{ state, update }} {...props} />
    }

    return [ctx, Provider] as const
}

/**
 * Create a context with hook useReducer
 *
 * @param {Function} reducer
 * @param {Any} initialState
 * @return {Array} [Context, Provider]
 */
export function createReducerCtx<StateType, ActionType>(
    reducer: React.Reducer<StateType, ActionType>,
    initialState: StateType
) {
    const defaultDispatch: React.Dispatch<ActionType> = () => initialState // we never actually use this

    const ctx = React.createContext({
        state: initialState,
        dispatch: defaultDispatch // just to mock out the dispatch type and make it not optional
    })

    function Provider(props: React.PropsWithChildren<{}>) {
        const [state, dispatch] = React.useReducer<React.Reducer<StateType, ActionType>>(
            reducer,
            initialState
        )
        return <ctx.Provider value={{ state, dispatch }} {...props} />
    }

    return [ctx, Provider] as const
}
